/**
 * The link that opens a generated report on the Reports page (the copilot's report card, contract v1 2026-09-26):
 *
 *   /app/twin/reports?tab=generate&from=YYYY-MM-DD&to=YYYY-MM-DD&granularity=daily|weekly|monthly&generate=1
 *
 * The page has no tab named "generate": the generator (GeneratedReportPanel) is on Plant reports, so the link opens
 * that tab. The page reads the link once, when it opens: it fills the generator's window and grouping and generates
 * once, and drops the link's parameters from the address, so a reload or Back does not generate (and save) again.
 * A bad parameter is ignored, never a crash.
 */
import { isPlantDay } from "@/lib/plant-days";
import type { GeneratedReportSnapshot } from "./generated-report-cache";
import { GRANULARITIES, type ReportGranularity } from "./statistics";

export type ReportsTab = "plant" | "equipment" | "six_sigma";

/** The link's own parameters, which the page drops once it has read them. */
export const REPORT_LINK_PARAMS = ["from", "to", "granularity", "generate"] as const;

/** A report the copilot proposes (contract: report spec). */
export interface ReportLinkSpec {
  granularity: ReportGranularity;
  from: string;
  to: string;
}

/** The link that opens the generator with a report's window and grouping, generating once. */
export function reportUrl(spec: ReportLinkSpec): string {
  const q = new URLSearchParams({
    tab: "generate",
    from: spec.from,
    to: spec.to,
    granularity: spec.granularity,
    generate: "1",
  });
  return `/app/twin/reports?${q.toString()}`;
}

/**
 * The same link without its generate step: it opens the generator on the report's window and grouping, where the
 * page shows the report already generated for it. An applied card keeps this form, so reopening it never generates
 * (and saves) another copy (#986 review, LOW 2).
 */
export function reportViewUrl(url: string): string {
  const u = new URL(url, "http://page");
  u.searchParams.delete("generate");
  return u.pathname + u.search;
}

/**
 * Whether Reports shows its Six Sigma tab (the Navigation review, D7): hidden once the org is granted improvement
 * projects, whose server-side DMAIC replaces this browser-only one; "pending" while the live platform has not yet said
 * (#1224 review, LOW 1: the tab never shows and then vanishes). A read that failed counts as said: the tab shows, as it
 * did before D7. Without a live platform there is nothing to ask, and it shows.
 */
export function sixSigmaTabState(a: { live: boolean; settled: boolean; projectsOn: boolean }): "shown" | "hidden" | "pending" {
  if (!a.live) return "shown";
  if (!a.settled) return "pending";
  return a.projectsOn ? "hidden" : "shown";
}

/** The Reports tab a link opens: Plant reports (the generator's) for tab=generate and anything unknown. */
export function reportsTabOf(raw: string | null): ReportsTab {
  return raw === "equipment" || raw === "six_sigma" ? raw : "plant";
}

/** What a link asks of the generator: each bound and the grouping when good (else null), and whether to generate. */
export interface ReportRequest {
  from: string | null;
  to: string | null;
  granularity: ReportGranularity | null;
  generate: boolean;
}

/**
 * What the page's link asks of the generator, or null for a plain visit (nothing usable). Each bad parameter is
 * ignored alone; a window whose start is after its end is dropped whole.
 */
export function reportRequestOf(params: URLSearchParams): ReportRequest | null {
  let from = params.get("from");
  let to = params.get("to");
  if (!isPlantDay(from)) from = null;
  if (!isPlantDay(to)) to = null;
  if (from !== null && to !== null && from > to) {
    from = null;
    to = null;
  }
  const g = params.get("granularity");
  const granularity = GRANULARITIES.find((d) => d.key === g)?.key ?? null;
  const generate = params.get("generate") === "1";
  if (from === null && to === null && granularity === null && !generate) return null;
  return { from, to, granularity, generate };
}

/** Where the generator starts: its window, its grouping, the report it shows, and whether it generates once. */
export interface InitialReportState {
  granularity: ReportGranularity;
  from: string;
  to: string;
  snapshot: GeneratedReportSnapshot | null;
  autoGenerate: boolean;
}

/**
 * Where the generator starts. A plain visit restores the last report (the snapshot kept across navigation, as before).
 * A link fills its grouping, and its window once it names a bound (the window then comes from the link alone, never
 * half from the last report). The last report stays only where it is the report the form now describes, and never
 * when the link generates: a fresh report replaces it, and an older one is not shown meanwhile.
 */
export function initialReportState(
  cached: GeneratedReportSnapshot | null,
  request: ReportRequest | null,
): InitialReportState {
  if (!request) {
    return {
      granularity: cached?.granularity ?? "daily",
      from: cached?.from ?? "",
      to: cached?.to ?? "",
      snapshot: cached,
      autoGenerate: false,
    };
  }
  const linkedWindow = request.from !== null || request.to !== null;
  const granularity = request.granularity ?? cached?.granularity ?? "daily";
  const from = linkedWindow ? (request.from ?? "") : (cached?.from ?? "");
  const to = linkedWindow ? (request.to ?? "") : (cached?.to ?? "");
  const matches = cached !== null && cached.granularity === granularity && cached.from === from && cached.to === to;
  return { granularity, from, to, snapshot: matches && !request.generate ? cached : null, autoGenerate: request.generate };
}
